import React, { useCallback, useEffect, useRef } from 'react'
import analytics, { convertCentsToEuro, getDonationFormType } from '@betterplace/product-analytics'
import { Button } from 'shared'
import { Config, FormValues } from 'donate/types'
import { FormikCents, PaypalButton } from './'
import { FormikContextType, connect } from 'formik'

function useTrackSubmitForm(formik: FormikContextType<FormValues>, config: Config) {
  const trackSubmitFormFactory = useCallback(
    () => () => {
      if (formik.isValid) {
        analytics.donationSubmitted({
          donation_amount: convertCentsToEuro(formik.values.amount_cents),
          recurring_donation: formik.values.interval,
          payment_method: formik.values.payment_method,
          codonation_amount: convertCentsToEuro(formik.values.codonation_cents),
          show_amount_selected: formik.values.show_amount,
          show_name_selected: !!(formik.values.show_user || formik.values.display_name),
          donation_receipt_requested: formik.values.receipt_desired,
          platform_newsletter_selected: formik.values.newsletter,
          comment_added: !!formik.values.message,
          id: config.receiverId,
          receiver_type: config.receiverType,
          donation_form_type: getDonationFormType({
            channel: formik.values.channel,
            receiverType: formik.values.receiver_type,
            receiverId: formik.values.receiver_id,
            showCodonation: !!formik.values.show_codonation,
          }),
        })
      } else {
        analytics.donationErrorMessageDisplayed({
          donation_form_type: getDonationFormType({
            channel: formik.values.channel,
            receiverType: formik.values.receiver_type,
            receiverId: formik.values.receiver_id,
            showCodonation: !!formik.values.show_codonation,
          }),
          id: config.receiverId,
          receiver_type: formik.values.receiver_type,
          error: JSON.stringify(formik.errors),
          errors: formik.errors,
        })
      }
    },
    [
      formik.isValid,
      formik.values.amount_cents,
      formik.values.interval,
      formik.values.payment_method,
      formik.values.codonation_cents,
      formik.values.show_amount,
      formik.values.show_user,
      formik.values.display_name,
      formik.values.receipt_desired,
      formik.values.newsletter,
      formik.values.message,
      formik.values.channel,
      formik.values.receiver_type,
      formik.values.receiver_id,
      formik.values.show_codonation,
      formik.errors,
      config.receiverId,
      config.receiverType,
    ]
  )

  const ref = useRef<() => void>(trackSubmitFormFactory())

  useEffect(() => {
    ref.current = trackSubmitFormFactory()
  }, [trackSubmitFormFactory])

  const trackSubmitForm = useCallback(() => ref.current(), [])

  return trackSubmitForm
}

export const SubmitButton: React.FC<{ config: Config }> = connect<{ config: Config }, FormValues>(
  ({ formik, config }) => {
    const trackSubmitForm = useTrackSubmitForm(formik, config)

    if (formik.values.payment_method === 'paypal') {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <PaypalButton clientId={config.paypalClientId!} onClick={trackSubmitForm} />
    }

    const loading = formik.isSubmitting || formik.values._redirecting
    const classNames = 'btn-large btn-primary submit-donation-button'

    if (formik.values.payment_method === 'direct_deposit') {
      return (
        <Button
          loading={loading}
          className={classNames}
          onClick={trackSubmitForm}
          data-testid="donate-form-donate-button"
        >
          {i18n.t('donate.submit_button.direct_deposit')}
        </Button>
      )
    }

    return (
      <Button
        loading={loading}
        className={classNames}
        onClick={trackSubmitForm}
        data-testid="donate-form-donate-button"
      >
        <span>{i18n.t('donate.submit_button.default.before_amount')}</span>
        <FormikCents fields={['amount_cents', 'codonation_cents']} />
        <span>{i18n.t('donate.submit_button.default.after_amount')}</span>
      </Button>
    )
  }
)
